import React, { useContext, useState } from 'react'
import './index.scss'
import { observer } from 'mobx-react'
import InputText from '@Components/Fields/InputText'
import Button from '@Components/Buttons/Button'
import DatePickerComponent from '@Components/Fields/DatePicker'
import { setItemToArrayByIndex } from '@App/helpers/work-entities/removers'
import { StoresContext } from '@App/contexts'
import { WorkModel } from '@Entities/work/work'
import { RecordingModel } from '@Entities/work/recording-model'

interface EditRecordingProps {
    editingRecording: RecordingModel
    closePopupFunc(): void
}

interface FieldsState {
    title: string // max length 500
    titleError: string
    artistName: string // max length 500
    artistNameError: string
    label: string
    labelError: string
    isrc: string | null // length 12
    isrcError: string
    duration: string | null
    releaseDate: string | null
    isDisabled: boolean
}

const EditRecording = ({ editingRecording, closePopupFunc }: EditRecordingProps) => {
    const { workStore } = useContext(StoresContext)

    const [fields, setFields] = useState<FieldsState>({
        title: editingRecording.title,
        titleError: '',
        artistName: editingRecording.artistName,
        artistNameError: '',
        label: '',
        labelError: '',
        isrc: editingRecording.isrc,
        isrcError: '',
        duration: editingRecording.duration,
        releaseDate: editingRecording.releaseDate,
        isDisabled: true,
    })

    const validate = (next: FieldsState): FieldsState => {
        const isValid = next.title && !next.titleError && next.artistName && !next.artistNameError && !next.isrcError
        return { ...next, isDisabled: !isValid }
    }

    const getDate = (date: string) => {
        setFields((prev) => validate({ ...prev, releaseDate: date }))
    }

    const getValue = (rawValue: string, id: string) => {
        const value = rawValue.trim()

        setFields((prev) => {
            const next = { ...prev }

            switch (id) {
                case 'title':
                    next.titleError = value.length > 200 ? 'Max length 200 symbol' : ''
                    next.title = value
                    break
                case 'artistName':
                    next.artistNameError = value.length > 60 ? 'Max length 60 symbol' : ''
                    next.artistName = value
                    break
                case 'isrc':
                    next.isrcError = value.length && value.length !== 12 ? 'ISRC length must be 12 characters' : ''
                    next.isrc = value
                    break
                case 'label':
                    next.labelError = value.length > 60 ? 'Max length 60 symbol' : ''
                    next.label = value
                    break
                case 'duration':
                    next.duration = value
                    break
                default:
                    break
            }

            return validate(next)
        })
    }

    const editRecording = () => {
        const workDraftFromLocal = workStore.workDraftLocal
        const editingRecordingsId = editingRecording.localId

        if (!workDraftFromLocal || !editingRecordingsId) {
            return
        }

        const recordings = workDraftFromLocal.recordings
        const indexOfRecording = recordings
            .map((recording: RecordingModel) => recording.localId)
            .indexOf(editingRecordingsId)
        const updatedRecording: RecordingModel = {
            localId: editingRecordingsId,
            title: fields.title, // REQUIRED FIELD
            artistName: fields.artistName, // REQUIRED FIELD
            isrc: fields.isrc ? fields.isrc : null,
            duration: fields.duration ? fields.duration : null,
            label: fields.label ? fields.label : null,
            releaseDate: fields.releaseDate ? fields.releaseDate : null,
        }

        const updatedRecordingsArray = setItemToArrayByIndex(recordings, +indexOfRecording, updatedRecording)

        const updatedWorkDraft: WorkModel = {
            ...workDraftFromLocal,
            recordings: updatedRecordingsArray,
        }

        workStore.setWorkDraftToLocal(updatedWorkDraft)
        closePopupFunc()
    }

    return (
        <div className="recording-sub-form-component">
            <h2>Edit recording</h2>
            <div className="inputs-field">
                <InputText
                    mainColor
                    id="title"
                    label="Recording Title"
                    assistiveText="Required"
                    errorMessage={fields.titleError}
                    value={fields.title}
                    onChange={getValue}
                />
                <InputText
                    mainColor
                    id="artistName"
                    label="Recording Artist Name"
                    assistiveText="Required"
                    errorMessage={fields.artistNameError}
                    value={fields.artistName}
                    onChange={getValue}
                />
                <InputText
                    mainColor
                    id="isrc"
                    label="ISRC"
                    errorMessage={fields.isrcError}
                    value={fields.isrc}
                    onChange={getValue}
                />
                <InputText mainColor id="label" label="Label" errorMessage={fields.labelError} onChange={getValue} />
                <InputText mainColor id="duration" label="Duration" value={`${fields.duration}`} onChange={getValue} />
                <DatePickerComponent id="releaseDate" label="Release Date" onChange={getDate} />
            </div>
            <Button mainColor type="primary" onClick={editRecording} disabled={fields.isDisabled}>
                Edit recording
            </Button>
        </div>
    )
}

export default observer(EditRecording)
